import type { SpaceSlug } from '@toolproof-core/visualization/_contract/key';

import type { EpisodeRing } from '@/adapters/episode/_contract/config';

type Vec3 = [number, number, number];

/**
 * World units are metres. The viewer arrives at the hub; each space's episodes form a carousel of
 * outward-facing doorways out on a larger ring, so the carousels are reached by walking around them.
 */
export const EPISODE_HUB_GEOMETRY = {
    eyeHeight: 1.6,
    hubRadius: 16,
    /** Straight ahead of a viewer facing -Z. */
    frontAngle: -Math.PI / 2,
    portalWidth: 1.2,
    portalHeight: 2,
    /** Opening centre height; the opening spans 0.15–2.15 m so any standing head passes through. */
    portalCenterY: 1.15,
    portalGap: 0.7,
    minCarouselRadius: 2.5,
    labelY: 3.4,
    labelWidth: 3,
    labelHeight: 0.7,
    labelFontSize: 0.36,
    /** How far in front of the portal they left through a returning viewer arrives. */
    returnArrivalDistance: 2.5,
} as const;

export type EpisodeHubGeometry = typeof EPISODE_HUB_GEOMETRY;

export type EpisodePortalPlacement = {
    handle: string;
    title: string;
    center: Vec3;
    /** Outward from the carousel centre: the side the viewer approaches from. */
    facing: Vec3;
};

export type EpisodeCarouselPlacement = {
    spaceSlug: SpaceSlug;
    label: string;
    /** Floor-level centre of the carousel. */
    center: Vec3;
    radius: number;
    /** Angle, around the carousel centre, of the side nearest the hub; the first portal and the label sit there. */
    hubwardAngle: number;
    portals: EpisodePortalPlacement[];
};

export function carouselRadius(portalCount: number, geometry: EpisodeHubGeometry = EPISODE_HUB_GEOMETRY): number {
    const circumference = portalCount * (geometry.portalWidth + geometry.portalGap);
    return Math.max(geometry.minCarouselRadius, circumference / (2 * Math.PI));
}

/** Spaces without episodes are dropped; the first carousel is straight ahead of the hub and the rest follow evenly. */
export function layoutEpisodeHub(
    rings: readonly EpisodeRing[],
    geometry: EpisodeHubGeometry = EPISODE_HUB_GEOMETRY,
): EpisodeCarouselPlacement[] {
    const populated = rings.filter((ring) => ring.episodes.length > 0);
    return populated.map((ring, index) => {
        const bearing = geometry.frontAngle + index * 2 * Math.PI / populated.length;
        const center: Vec3 = [Math.cos(bearing) * geometry.hubRadius, 0, Math.sin(bearing) * geometry.hubRadius];
        const radius = carouselRadius(ring.episodes.length, geometry);
        const hubwardAngle = bearing + Math.PI;
        const step = 2 * Math.PI / ring.episodes.length;
        return {
            spaceSlug: ring.spaceSlug,
            label: ring.label,
            center,
            radius,
            hubwardAngle,
            portals: ring.episodes.map((episode, slot) => {
                const angle = hubwardAngle + slot * step;
                const facing: Vec3 = [Math.cos(angle), 0, Math.sin(angle)];
                return {
                    handle: episode.handle,
                    title: episode.title,
                    center: [center[0] + facing[0] * radius, geometry.portalCenterY, center[2] + facing[2] * radius],
                    facing,
                };
            }),
        };
    });
}
